// __EQUIP_COMPAT__ (2026-10-04) Settings > Equipment rules: the limits and the cargo-type ->
// equipment mapping the hard equipment warning uses (board AND hub bookings read the same
// row, public.equipment_rules 'main'). Office logins save; everyone signed in reads.
import React, { useEffect, useState } from 'react';
import {
  DEFAULT_RULES, normalizeRules, EQUIPMENT_CLASSES, EQUIPMENT_FEATURES, TRUCK_TYPES, TRAILER_TYPES,
  ISSUE_CODES, DEFAULT_SEVERITY, DEFAULT_TIMING, RESTRICTION_FIELDS, halfLoadOn, todayISO,
  LOADING_METHODS, COVER_TYPES, DEFAULT_LOADING_MATRIX,
} from '../utils/equipmentCompat';
import { fetchRules, saveRules, invalidateEquipmentCache, fetchFleet } from '../services/equipmentService';
import { CityZonesEditor } from './equipment/OperatingLimits';   // __OPERATING_LIMITS__

const box = { backgroundColor: 'var(--surface-2)', padding: '0.9rem', borderRadius: '5px', marginBottom: '1rem' };
const input = { padding: '0.45rem', backgroundColor: 'var(--surface-1)', border: '1px solid var(--border-default)', borderRadius: '4px', color: 'var(--text-primary)', fontSize: '0.88rem' };
const lab = { display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' };
const chk = { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-secondary)', marginRight: '0.8rem' };

// __EQUIP_V2__ (2026-10-04) Henry: "Sometimes we can negotiate those; overhang can be accommodated
// depending what it is." Every issue is a warning by default (one tap, reason optional);
// each can be set to block (reason required) or info (hint only) here.
const SEV_LABEL = { warn: 'Warn (one tap, reason optional)', block: 'Block (reason required)', info: 'Info only (no popup)' };
const TIMING_LABELS = {
  company_day_hours: 'Company daily limit (h on duty)',
  hos_driving_hours: 'HOS: driving per day (h)',
  hos_on_duty_hours: 'HOS: on duty per day (h)',
  hos_window_hours: 'HOS: elapsed window (h)',
  hos_off_hours: 'HOS: consecutive off before the day (h)',
};

const ALL_TYPES = [
  ...Object.entries(TRUCK_TYPES).map(([k, t]) => ({ k, label: 'Truck: ' + t.label })),
  ...Object.entries(TRAILER_TYPES).filter(([k]) => !TRUCK_TYPES[k]).map(([k, t]) => ({ k, label: 'Trailer: ' + t.label })),
];

export default function EquipmentRulesSettings() {
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [fleet, setFleet] = useState({ trucks: [], trailers: [] });

  useEffect(() => {
    invalidateEquipmentCache();
    fetchRules(true).then(setRules).catch(() => setRules(normalizeRules(null)));
    fetchFleet(true).then((f) => setFleet(f || { trucks: [], trailers: [] })).catch(() => {});
  }, []);
  if (!rules) return <div style={{ color: 'var(--text-muted)' }}>Loading equipment rules...</div>;
  const setSev = (code, v) => {
    const next = { ...(rules.severities || {}) };
    if (v === (DEFAULT_SEVERITY[code] || 'warn')) delete next[code]; else next[code] = v;
    set('severities', next);
  };
  const setTiming = (k, v) => set('timing', { ...(rules.timing || {}), [k]: v });
  const restr = rules.restrictions || [];
  const setRestr = (i, patch) => set('restrictions', restr.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const hl = rules.half_load || { seasons: [], payload_pct: null, payload_cap_lb: null, note: '' };
  const setHl = (patch) => set('half_load', { ...hl, ...patch });
  const setSeason = (i, patch) => setHl({ seasons: hl.seasons.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const unitOptions = (kind) => (kind === 'trailer' ? (fleet.trailers || []).map((t) => ({ id: t.id, label: t.trailer_number || String(t.id).slice(0, 8) }))
    : (fleet.trucks || []).map((t) => ({ id: t.id, label: t.truck_number || String(t.id).slice(0, 8) })))
    .sort((a, b) => a.label.localeCompare(b.label));
  const inSeason = halfLoadOn(normalizeRules(rules), todayISO());

  const set = (k, v) => setRules({ ...rules, [k]: v });
  const setRule = (i, patch) => set('cargo_rules', rules.cargo_rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const toggleIn = (i, key, val) => {
    const cur = rules.cargo_rules[i][key] || [];
    setRule(i, { [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] });
  };
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const want = normalizeRules(rules);
      const dropped = (rules.restrictions || []).length - want.restrictions.length
        + ((rules.half_load && rules.half_load.seasons) || []).length - want.half_load.seasons.length;
      const clean = await saveRules(rules);
      setRules(clean);
      setMsg({ ok: true, text: 'Saved. The board and hub bookings use these rules from now on.'
        + (dropped > 0 ? ' ' + dropped + ' incomplete restriction/season line(s) were left out (each needs a start date, a value and, for one unit, the unit; a season needs both dates).' : '') });
    } catch (e) {
      setMsg({ ok: false, text: 'Not saved: ' + (e.message || String(e)) });
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ ...box, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        These rules decide the <strong>equipment warning</strong> shown whenever a job or a route is assigned to a driver
        (board drag, Quick Assign, Edit, Swap, Route Builder Finalize, hub booking, dispatch app). Issues are <strong>warnings</strong> by
        default: "Assign anyway" is one tap and the reason is optional (kept on the job and in the audit log, with the list of
        warnings you accepted). Set an issue to Block below to require a reason. Only a deleted driver or unit cannot be assigned.
        Sizes in feet, weights in pounds.
      </div>

      <div style={box}>
        <h4 style={{ marginTop: 0, color: 'var(--accent-primary)' }}>Limits</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '0.75rem' }}>
          <div><label style={lab}>Legal width (ft)</label><input type="number" step="any" style={input} value={rules.legal_width_ft} onChange={(e) => set('legal_width_ft', e.target.value)} /></div>
          <div><label style={lab}>Legal height (ft)</label><input type="number" step="any" style={input} value={rules.legal_height_ft} onChange={(e) => set('legal_height_ft', e.target.value)} /></div>
          <div><label style={lab}>Open-deck overhang allowed (ft)</label><input type="number" step="any" style={input} value={rules.open_deck_overhang_ft} onChange={(e) => set('open_deck_overhang_ft', e.target.value)} /></div>
          <div><label style={lab}>"Near capacity" warning at (%)</label><input type="number" step="1" style={input} value={rules.near_capacity_pct} onChange={(e) => set('near_capacity_pct', e.target.value)} /></div>
        </div>
      </div>

      <div style={box}>
        <h4 style={{ marginTop: 0, color: 'var(--accent-primary)' }}>How each issue is treated</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: '0.4rem 1rem' }}>
          {Object.entries(ISSUE_CODES).map(([code, label]) => {
            const cur = (rules.severities || {})[code] || DEFAULT_SEVERITY[code] || 'warn';
            return (
              <div key={code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <select style={{ ...input, padding: '0.25rem', fontSize: '0.78rem', borderColor: cur === 'block' ? 'var(--color-danger)' : 'var(--border-default)' }}
                  value={cur} onChange={(e) => setSev(code, e.target.value)}>
                  {['warn', 'block', 'info'].map((v) => <option key={v} value={v}>{SEV_LABEL[v]}</option>)}
                </select>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
          Cargo type rules (below) each have their own setting. A driver or unit that no longer exists always stops the assignment.
        </div>
      </div>

      <div style={box}>
        <h4 style={{ marginTop: 0, color: 'var(--accent-primary)' }}>Driver's day and hours of service (warnings)</h4>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
          The check adds the driver's work already assigned that day (route plan times, else the job's estimated hours, else 2 h)
          to the new job or route. Defaults: company 12 h; Canadian federal commercial HOS south of 60: 13 h driving, 14 h on duty,
          16 h window, 8 h off. The 70 h / 7-day cycle is not checked.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '0.75rem' }}>
          {Object.keys(DEFAULT_TIMING).map((k) => (
            <div key={k}><label style={lab}>{TIMING_LABELS[k]}</label>
              <input type="number" step="any" style={input} value={(rules.timing || {})[k] ?? ''} placeholder={String(DEFAULT_TIMING[k])} onChange={(e) => setTiming(k, e.target.value)} /></div>
          ))}
        </div>
      </div>

      <div style={box}>
        <h4 style={{ marginTop: 0, color: 'var(--accent-primary)' }}>Temporary restrictions</h4>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
          A limit that applies only between two dates (leave the end blank for "until removed"). The check uses whatever is in
          effect on the <strong>job's date</strong>. For every unit: legal width / height, overhang, most weight. For one truck or trailer:
          payload, deck size, cargo height, or unavailable (out of area, ...).
        </div>
        {restr.map((x, i) => {
          const f = RESTRICTION_FIELDS[x.field] || {};
          return (
            <div key={x.id || i} style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-default)' }}>
              <div><label style={lab}>Limit</label>
                <select style={input} value={x.field} onChange={(e) => setRestr(i, { field: e.target.value })}>
                  {Object.entries(RESTRICTION_FIELDS).map(([k, v]) => <option key={k} value={k}>{(v.scope === 'unit' ? 'One unit: ' : 'Every unit: ') + v.label}</option>)}
                </select></div>
              {f.scope === 'unit' && (
                <>
                  <div><label style={lab}>Kind</label>
                    <select style={input} value={x.unit_kind || 'truck'} onChange={(e) => setRestr(i, { unit_kind: e.target.value, unit_id: '' })}>
                      <option value="truck">Truck</option><option value="trailer">Trailer</option>
                    </select></div>
                  <div><label style={lab}>Unit</label>
                    <select style={input} value={x.unit_id || ''} onChange={(e) => setRestr(i, { unit_id: e.target.value })}>
                      <option value="">Choose...</option>
                      {unitOptions(x.unit_kind || 'truck').map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                    </select></div>
                </>
              )}
              {x.field !== 'unavailable' && (
                <div><label style={lab}>Value</label><input type="number" step="any" style={{ ...input, width: '90px' }} value={x.value ?? ''} onChange={(e) => setRestr(i, { value: e.target.value })} /></div>
              )}
              <div><label style={lab}>From</label><input type="date" style={input} value={x.start || ''} onChange={(e) => setRestr(i, { start: e.target.value })} /></div>
              <div><label style={lab}>To (blank = open)</label><input type="date" style={input} value={x.end || ''} onChange={(e) => setRestr(i, { end: e.target.value || null })} /></div>
              <div style={{ flex: 1, minWidth: '160px' }}><label style={lab}>Note</label><input style={{ ...input, width: '100%', boxSizing: 'border-box' }} value={x.note || ''} onChange={(e) => setRestr(i, { note: e.target.value })} /></div>
              <button type="button" onClick={() => set('restrictions', restr.filter((_, j) => j !== i))} style={{ ...input, cursor: 'pointer', color: 'var(--color-danger)' }}>Remove</button>
            </div>
          );
        })}
        <button type="button" style={{ ...input, cursor: 'pointer' }}
          onClick={() => set('restrictions', [...restr, { id: 'r' + Date.now(), field: 'payload_lb', unit_kind: 'truck', unit_id: '', value: '', start: todayISO(), end: null, note: '' }])}>
          + Add restriction
        </button>
      </div>

      <div style={{ ...box, borderLeft: inSeason ? '4px solid var(--color-warning)' : undefined }}>
        <h4 style={{ marginTop: 0, color: 'var(--accent-primary)' }}>Half-load season{inSeason ? ' (in effect today)' : ''}</h4>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
          Reduced-load period(s) each year - set the dates here. Jobs dated inside a season are checked against each truck's and
          trailer's OWN half-load limits (total and per-axle caps, entered on the unit in Trucks / Trailers; for a pair, the trailer's
          and the pair's together). A unit with no limits of its own uses the fleet-wide fallback below, if set - otherwise it has no
          half-load limit. A warning when over. The board shows a "Half-load season in effect" badge during it.
        </div>
        {hl.seasons.map((x, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-end', marginBottom: '0.4rem' }}>
            <div><label style={lab}>From</label><input type="date" style={input} value={x.start || ''} onChange={(e) => setSeason(i, { start: e.target.value })} /></div>
            <div><label style={lab}>To</label><input type="date" style={input} value={x.end || ''} onChange={(e) => setSeason(i, { end: e.target.value })} /></div>
            <button type="button" onClick={() => setHl({ seasons: hl.seasons.filter((_, j) => j !== i) })} style={{ ...input, cursor: 'pointer', color: 'var(--color-danger)' }}>Remove</button>
          </div>
        ))}
        <button type="button" style={{ ...input, cursor: 'pointer', marginBottom: '0.6rem' }} onClick={() => setHl({ seasons: [...hl.seasons, { start: '', end: '' }] })}>+ Add season dates</button>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '0.75rem' }}>
          <div><label style={lab}>Fallback: payload allowed (% of normal)</label><input type="number" step="any" style={input} value={hl.payload_pct ?? ''} placeholder="e.g. 50" onChange={(e) => setHl({ payload_pct: e.target.value })} /></div>
          <div><label style={lab}>Fallback: ...or most weight on any unit (lb)</label><input type="number" step="any" style={input} value={hl.payload_cap_lb ?? ''} onChange={(e) => setHl({ payload_cap_lb: e.target.value })} /></div>
          <div style={{ gridColumn: 'span 2' }}><label style={lab}>Note (shown in the warning and badge)</label><input style={{ ...input, width: '100%', boxSizing: 'border-box' }} value={hl.note || ''} onChange={(e) => setHl({ note: e.target.value })} /></div>
        </div>
      </div>

      {/* __SITE_LOADING__ (2026-10-17) Henry: "Make the rule matrix a Settings table so Henry can extend it." */}
      <div style={box}>
        <h4 style={{ marginTop: 0, color: 'var(--accent-primary)' }}>Site loading (address book "Loading at this site")</h4>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
          Each address-book entry (hub address book, dispatch app Directory &gt; Addresses, portal places) can say how that site loads:
          Dock, Overhead crane, Yard lift truck (none ticked = Unspecified, never a warning). A stop at a site whose methods include
          none that the unit's body / cover takes gives the <b>{ISSUE_CODES.site_loading}</b> warning. Rows: Open deck (every open-deck
          truck or trailer, the crane truck included), Rolling tarp, Curtain side; add a row for any other truck / trailer type.
          {' '}{(rules.sites || []).length} site(s) have a loading method set.
        </div>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead><tr><th style={{ textAlign: 'left', padding: '0.25rem 0.6rem' }}>Body / cover</th>
            {Object.entries(LOADING_METHODS).map(([m, l]) => <th key={m} style={{ padding: '0.25rem 0.6rem' }}>{l}</th>)}<th /></tr></thead>
          <tbody>
            {Object.keys(rules.loading_matrix || DEFAULT_LOADING_MATRIX).map((k) => {
              const lm = rules.loading_matrix || DEFAULT_LOADING_MATRIX;
              const label = COVER_TYPES[k] || (TRUCK_TYPES[k] || TRAILER_TYPES[k] || { label: k }).label;
              return (
                <tr key={k}>
                  <td style={{ padding: '0.25rem 0.6rem', color: 'var(--text-secondary)' }}>{label}</td>
                  {Object.keys(LOADING_METHODS).map((m) => (
                    <td key={m} style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={(lm[k] || []).includes(m)} onChange={() => {
                        const cur = lm[k] || [];
                        set('loading_matrix', { ...lm, [k]: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m] });
                      }} />
                    </td>
                  ))}
                  <td>{!DEFAULT_LOADING_MATRIX[k] && <button type="button" style={{ ...input, cursor: 'pointer', padding: '0.15rem 0.5rem' }}
                    onClick={() => { const next = { ...lm }; delete next[k]; set('loading_matrix', next); }}>Remove</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select style={input} value="" onChange={(e) => { const k = e.target.value; if (!k) return; const lm = rules.loading_matrix || DEFAULT_LOADING_MATRIX; if (!lm[k]) set('loading_matrix', { ...lm, [k]: ['dock'] }); }}>
            <option value="">+ Add a row for a unit type...</option>
            {ALL_TYPES.filter((t) => !(rules.loading_matrix || DEFAULT_LOADING_MATRIX)[t.k]).map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
          </select>
          <button type="button" style={{ ...input, cursor: 'pointer' }} onClick={() => set('loading_matrix', { ...DEFAULT_LOADING_MATRIX })}>Defaults</button>
        </div>
      </div>

      {/* __OPERATING_LIMITS__ (2026-10-17) the city zones "No city runs" checks (each truck's limits: Trucks > Edit). Saved by
          their own button, right away - the page's Save below keeps them as they are. */}
      <div style={box}>
        <CityZonesEditor />
      </div>

      <h4 style={{ color: 'var(--accent-primary)', marginBottom: '0.4rem' }}>Cargo type rules</h4>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
        A job whose cargo type matches a line below (case, spaces and punctuation ignored; "Manlift" also finds "Manlift / boom lift"; a line starting with = matches only that exact type;
        a rule with "Also look in the job notes" ticked also matches words in the notes, e.g. "No forklift" for self-unload)
        must go on a unit of an allowed kind and with at least one of the needed features. The first matching rule applies.
      </div>
      {rules.cargo_rules.map((r, i) => (
        <div key={r.id + i} style={{ ...box, borderLeft: '4px solid ' + (r.severity === 'block' ? 'var(--color-danger)' : r.severity === 'info' ? 'var(--border-default)' : 'var(--color-warning)') }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
            <div style={{ flex: 2, minWidth: '180px' }}><label style={lab}>Rule name</label><input style={{ ...input, width: '100%' }} value={r.name} onChange={(e) => setRule(i, { name: e.target.value })} /></div>
            <div><label style={lab}>When it does not fit</label>
              <select style={input} value={r.severity} onChange={(e) => setRule(i, { severity: e.target.value })}>
                {['warn', 'block', 'info'].map((v) => <option key={v} value={v}>{SEV_LABEL[v]}</option>)}
              </select>
            </div>
            <label style={chk}><input type="checkbox" checked={!!r.towed} onChange={(e) => setRule(i, { towed: e.target.checked })} />Towed, not carried</label>
            <label style={chk}><input type="checkbox" checked={!!r.match_notes} onChange={(e) => setRule(i, { match_notes: e.target.checked })} />Also look in the job notes</label>
            <button type="button" onClick={() => set('cargo_rules', rules.cargo_rules.filter((_, j) => j !== i))} style={{ ...input, cursor: 'pointer', color: 'var(--color-danger)' }}>Remove</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 2fr', gap: '0.75rem' }}>
            <div>
              <label style={lab}>Cargo types (one per line)</label>
              <textarea rows={6} style={{ ...input, width: '100%', boxSizing: 'border-box' }} value={r.cargo.join('\n')}
                onChange={(e) => setRule(i, { cargo: e.target.value.split('\n') })} />
            </div>
            <div>
              <label style={lab}>Allowed kinds of unit (none ticked = any)</label>
              <div>{Object.entries(EQUIPMENT_CLASSES).map(([k, l]) => (
                <label key={k} style={chk}><input type="checkbox" checked={r.allowed_classes.includes(k)} onChange={() => toggleIn(i, 'allowed_classes', k)} />{l}</label>
              ))}</div>
              <label style={{ ...lab, marginTop: '0.5rem' }}>...or these exact types</label>
              <div style={{ maxHeight: '90px', overflow: 'auto' }}>{ALL_TYPES.map((t) => (
                <label key={t.k} style={chk}><input type="checkbox" checked={r.allowed_types.includes(t.k)} onChange={() => toggleIn(i, 'allowed_types', t.k)} />{t.label}</label>
              ))}</div>
              <label style={{ ...lab, marginTop: '0.5rem' }}>Needs at least one of these features (none ticked = no feature needed)</label>
              <div>{Object.entries(EQUIPMENT_FEATURES).map(([k, l]) => (
                <label key={k} style={chk}><input type="checkbox" checked={r.needs_any_feature.includes(k)} onChange={() => toggleIn(i, 'needs_any_feature', k)} />{l}</label>
              ))}</div>
              <label style={{ ...lab, marginTop: '0.5rem' }}>Note shown in the warning</label>
              <input style={{ ...input, width: '100%', boxSizing: 'border-box' }} value={r.note} onChange={(e) => setRule(i, { note: e.target.value })} />
            </div>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" style={{ ...input, cursor: 'pointer' }}
          onClick={() => set('cargo_rules', [...rules.cargo_rules, { id: 'rule' + Date.now(), name: 'New rule', severity: 'warn', towed: false, match_notes: false, cargo: [], allowed_types: [], allowed_classes: [], needs_any_feature: [], note: '' }])}>
          + Add rule
        </button>
        <button type="button" style={{ ...input, cursor: 'pointer' }} onClick={() => { if (window.confirm('Put every equipment rule back to the built-in defaults? (Not saved until you press Save.)')) setRules(normalizeRules(DEFAULT_RULES)); }}>
          Reset to defaults
        </button>
        <button type="button" disabled={busy} onClick={save} style={{ padding: '0.55rem 1.2rem', backgroundColor: 'var(--accent-primary)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: busy ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
          {busy ? 'Saving...' : 'Save equipment rules'}
        </button>
        {msg && <span style={{ fontSize: '0.85rem', color: msg.ok ? 'var(--color-success)' : 'var(--color-danger)' }}>{msg.text}</span>}
      </div>
    </div>
  );
}
